import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
	Form,
	FormGroup,
	Label,
	Input,
	Button,
	FormFeedback,
} from "reactstrap";
import NumberFormat from "react-number-format";
import dayjs from "dayjs";
import { TecAssuredStore } from "../../../../../../stores/TecAssuredStore";
import { initialAgreementDocuments } from "../../../../../../api/TecAssuredAPI";
import { useFromUser } from "../../../../../../stores/LocalStorageHelper";
import shallow from "zustand/shallow";
import useCurrentDealStore from "../../../../../../stores/DealStore";
import useBuildDeal from "../../../../hooks/useBuildDeal";
import { TecAssuredProductInputMap } from "./TecAssuredProductInputMap";
import { getValueByPath } from "./TecAssuredVehicleInputMap";
import useCurrentSaleStore from "../../../../../../stores/SaleStore";

const TecAssuredDynamicProductProperties = ({ onComplete }) => {
	const { register, control, handleSubmit, errors, setValue, trigger, reset } =
		useForm();
	const { productPropertiesResp, agreementReqBody, setAgreementReqBody } =
		TecAssuredStore();
	const locationID = useFromUser("locationID");
	const { warrantyList, editWarrantyList, dealRecNum, type, calculate } =
		useCurrentDealStore(
			(state) => ({
				warrantyList: state.warranty,
				editWarrantyList: state.editWarranty,
				dealRecNum: state.deal.ID,
				type: state.deal.type,
				calculate: state.calculate,
			}),
			shallow
		);
	const { editGap } = useCurrentSaleStore((state) => state, shallow);

	const getDeal = useBuildDeal();

	const handleFormatValue = (field, rawValue) => {
		const { Property, PropertyType } = field;
		switch (PropertyType) {
			case "Currency":
			case "Decimal":
			case "Integer":
				if (typeof rawValue === "string") {
					const cleaned = rawValue.replace(/[^0-9.-]+/g, "");
					return Number(cleaned || 0);
				}
				return Number(rawValue || 0);
			case "Boolean":
				return !!rawValue;
			case "Date":
				return dayjs(rawValue).format("YYYY-MM-DDTHH:mm:ss[Z]");
			default:
				return rawValue || "";
		}
	};

	const formatOutput = (data) => {
		const entry = productPropertiesResp?.item?.map((field) => {
			const { Property, PropertyType } = field;
			let value;
			if (PropertyType === "Period") {
				const y = Number(data[`${Property}_years`] || 0);
				const m = Number(data[`${Property}_months`] || 0);
				const d = Number(data[`${Property}_days`] || 0);
				value = `P${y}Y${m}M${d}D`;
			} else {
				value = handleFormatValue(field, data[Property]);
			}

			let typedValue = {};
			if (PropertyType === "Currency") typedValue = { CurrencyValue: value };
			else if (PropertyType === "Decimal") typedValue = { DecimalValue: value };
			else if (PropertyType === "Integer") typedValue = { IntegerValue: value };
			else if (PropertyType === "Date") typedValue = { DateValue: value };
			else if (PropertyType === "Period") typedValue = { PeriodValue: value };
			else if (PropertyType === "Boolean") typedValue = { BooleanValue: value };
			else typedValue = { StringValue: value };

			return {
				key: Property,
				value: {
					PropertyType,
					...typedValue,
				},
			};
		});

		// Set all option prices to zero
		const optionsZeroed =
			agreementReqBody?.Agreement?.Products?.Options?.map((opt) => ({
				...opt,
				Amount: 0,
			})) || [];

		// PRICING
		// Now is the time To adjust the price:
		// - Set the product's Amount field to the desired price
		// - Set the price of the desired options to 0
		// - The total is calculated as the sum of these values.
		const body = {
			...agreementReqBody,
			Agreement: {
				...agreementReqBody.Agreement,
				Products: {
					...agreementReqBody.Agreement.Products,
					Options: optionsZeroed,
					properties: { entry },
					Amount: Number(data.amount) || totalAmount,
				},
			},
		};

		console.log(body);

		initialAgreementDocuments(body)
			.then((res) => {
				console.log(res.data);
				setAgreementReqBody({
					locationId: locationID,
					Agreement: res.data,
				});
				if (onComplete) onComplete();
			})
			.catch((error) => {
				console.error("Error initializing agreement documents:", error);
				throw error;
			});
	};

	const renderField = (field) => {
		const { Property, Name, PropertyType, Extension, Required } = field;
		const isRequired = Required === "true";

		if (Extension?.Values?.length > 0) {
			return (
				<Controller
					control={control}
					name={Property}
					rules={{ required: isRequired }}
					render={({ onChange, value, onBlur }) => (
						<Input
							type="select"
							id={Property}
							name={Property}
							value={value || ""}
							onChange={(e) => onChange(e.target.value)}
							onBlur={onBlur}
							invalid={!!errors[Property]}
						>
							<option value="">Select {Name}</option>
							{Extension.Values.map((opt) => (
								<option key={opt.Key} value={opt.Value}>
									{opt.Key}
								</option>
							))}
						</Input>
					)}
				/>
			);
		}

		const commonProps = {
			name: Property,
			innerRef: register({ required: Required === "true" }),
			invalid: !!errors[Property],
			onChange: (e) => {
				setValue(Property, e.target.value);
				trigger(Property);
			},
		};

		if (PropertyType === "Period") {
			return (
				<div className="d-flex gap-2">
					<Input
						type="number"
						min="0"
						name={`${Property}_years`}
						innerRef={register({ required: true })}
						placeholder="Years"
						invalid={!!errors[`${Property}_years`]}
					/>
					<Input
						type="number"
						min="0"
						name={`${Property}_months`}
						innerRef={register({ required: true })}
						placeholder="Months"
						invalid={!!errors[`${Property}_months`]}
					/>
					<Input
						type="number"
						min="0"
						name={`${Property}_days`}
						innerRef={register({ required: true })}
						placeholder="Days"
						invalid={!!errors[`${Property}_days`]}
					/>
				</div>
			);
		}

		if (PropertyType === "Currency") {
			return (
				<NumberFormat
					{...commonProps}
					customInput={Input}
					thousandSeparator
					prefix="$"
					decimalScale={2}
					allowNegative={false}
				/>
			);
		}

		if (PropertyType === "Decimal") {
			return (
				<NumberFormat
					{...commonProps}
					customInput={Input}
					thousandSeparator
					decimalScale={6}
					allowNegative={false}
				/>
			);
		}

		if (PropertyType === "Integer") {
			return (
				<NumberFormat
					{...commonProps}
					customInput={Input}
					allowNegative={false}
					decimalScale={0}
				/>
			);
		}

		if (PropertyType === "Boolean") {
			return (
				<div className="form-check form-switch">
					<Input
						type="checkbox"
						id={Property}
						name={Property}
						innerRef={register({ required: Required === "true" })}
						className="form-check-input"
						onChange={(e) => {
							setValue(Property, e.target.checked);
							trigger(Property);
						}}
					/>
					<Label className="form-check-label" for={Property}>
						{`Yes / No`}
					</Label>
				</div>
			);
		}

		if (PropertyType === "Date") {
			return <Input type="date" {...commonProps} />;
		}

		// Default: String
		return <Input type="text" {...commonProps} />;
	};

	const baseAmount = Number(agreementReqBody?.Agreement?.Products?.Amount || 0);

	const optionsAmount = (agreementReqBody?.Agreement?.Products?.Options || [])
		.map((opt) => Number(opt?.Amount || 0))
		.reduce((sum, amt) => sum + amt, 0);

	const totalAmount = baseAmount + optionsAmount;

	const formatCurrency = (amount) => {
		const num = parseFloat(amount);
		return isNaN(num)
			? amount
			: new Intl.NumberFormat("en-US", {
					style: "currency",
					currency: "USD",
			  }).format(num);
	};

	const handleAmountBlur = (value) => {
		const currentProductID = agreementReqBody?.Agreement?.Products?.ProductID;
		if (!currentProductID) return;

		const productTypeId = Number(
			agreementReqBody.Agreement.Products.ProductTypeID
		);

		// this is gap which is stored on a different table
		if (productTypeId === 4) {
			editGap(parseFloat(value) || 0);
			calculate();
			return;
		}

		// Find the warranty with matching ProductID
		const warrantyIndex = warrantyList.findIndex(
			(warranty) => warranty.ProductID === currentProductID
		);

		if (warrantyIndex !== -1) {
			// Create a copy of the warranty list
			const updatedWarrantyList = [...warrantyList];
			// Update the amount for the matching warranty
			updatedWarrantyList[warrantyIndex] = {
				...updatedWarrantyList[warrantyIndex],
				amount: parseFloat(value) || 0,
			};
			// Update the warranty list in the store
			editWarrantyList(updatedWarrantyList);
			// Recalculate
			calculate();
		}
	};

	useEffect(() => {
		if (totalAmount > 0) {
			setValue("amount", totalAmount.toFixed(2));
		}
	}, [totalAmount, setValue]);

	useEffect(() => {
		if (
			!productPropertiesResp?.item ||
			!productPropertiesResp?.tpaDetails?.tpaName
		)
			return;

		const deal = getDeal();
		const tpaName = productPropertiesResp.tpaDetails.tpaName;
		const productTypeId = Number(
			agreementReqBody.Agreement.Products.ProductTypeID
		);

		const mapping = TecAssuredProductInputMap?.[tpaName]?.[productTypeId] || [];
		const defaultValues = {};

		mapping.forEach((mapItem) => {
			const field = productPropertiesResp.item.find(
				(f) => f.Property === mapItem.Property
			);
			if (field) {
				let value = getValueByPath(deal, mapItem.DC);
				if (typeof mapItem.transform === "function") {
					value = mapItem.transform(value, deal);
				}
				if (value !== undefined) {
					defaultValues[mapItem.Property] = value;
				}
			}
		});

		console.log("Default product property values:", defaultValues);

		// Set the form values
		Object.entries(defaultValues).forEach(([key, value]) => {
			setValue(key, value);
		});
	}, [productPropertiesResp, getDeal, setValue]);

	return (
		<Form onSubmit={handleSubmit(formatOutput)}>
			<FormGroup key={"amount"}>
				<Label for="amount">Amount - ({formatCurrency(totalAmount)})</Label>
				<Controller
					name="amount"
					control={control}
					rules={{ required: true }}
					render={({ value, onChange }) => (
						<span onFocus={(e) => e.target.select()}>
							<NumberFormat
								value={value || ""}
								onValueChange={({ value }) => onChange(value)}
								onBlur={() => handleAmountBlur(value)}
								customInput={Input}
								thousandSeparator
								prefix="$"
								decimalScale={2}
								allowNegative={false}
							/>
						</span>
					)}
				/>

				{errors.amount && (
					<FormFeedback className="d-block">Amount is required</FormFeedback>
				)}
			</FormGroup>
			{productPropertiesResp?.item.map((field) => {
				const { Property, Name } = field;
				return (
					<FormGroup key={Property}>
						<Label for={Property}>{Name}</Label>
						{renderField(field)}
						{errors[Property] && (
							<FormFeedback>This field is required</FormFeedback>
						)}
					</FormGroup>
				);
			})}

			<Button type="submit" color="primary">
				Submit
			</Button>
		</Form>
	);
};

export default TecAssuredDynamicProductProperties;
